import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, viewChild, ViewEncapsulation } from '@angular/core';
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { TabHeaderComponent } from '@app/shared/components/tab-header/tab-header.component';
import { IProjectTab } from '@app/shared/interfaces/iproject-tab';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogModule } from "@progress/kendo-angular-dialog";
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { LoaderComponent, LoaderSize, LoaderThemeColor, LoaderType, } from "@progress/kendo-angular-indicators";
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { addDays } from '@progress/kendo-date-math';
import { arrowRotateCwIcon, rotateIcon } from "@progress/kendo-svg-icons";
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, FirstDataRenderedEvent, GridApi, GridReadyEvent } from 'ag-grid-community';
import dayjs from 'dayjs';
import { surfaceDevPhaseSteps } from '../../enums/surface-dev-phase-steps';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { AppScheduleTaskSurfacedevPlanningComponent } from './app-schedule-task-surfacedev-planning.component';
import { SurfaceDevelopmentScheduleActivityDto } from './app-schedule-task-surfacedev.dtos';
import { SurfaceDevScheduleTaskGridOptions } from './app-schedule-task-surfacedev.gridoptions';
import { surfacedevService } from './app-schedule-task-surfacedev.service';

@Component({
  selector: 'app-schedule-task-surfacedev',
  templateUrl: './app-schedule-task-surfacedev.component.html',
  styleUrls: ['./app-schedule-task-surfacedev.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridAngular,
    AppScheduleTaskSurfacedevPlanningComponent,
    ButtonDirective,
    DialogModule,
    LoaderComponent,
    SVGIconComponent,
    TabHeaderComponent,
    TooltipDirective,
  ],
  providers: [
    surfacedevService,
  ],
})
export class AppScheduleTaskSurfacedevComponent {
  private cdr = inject(ChangeDetectorRef);
  private lookupPlusService = inject(LookupPlusService);
  private errorService = inject(ErrorService);
  private service = inject(surfacedevService);
  public environment = inject(PlexEnvironment);

  public icons = kendoIcons;

  readonly surfacedevPlanningComponent = viewChild(AppScheduleTaskSurfacedevPlanningComponent);

  public loader = {
    type: <LoaderType>'converging-spinner',
    themeColor: <LoaderThemeColor>'info',
    size: <LoaderSize>'small'
  };

  public dialogData = {
    resyncingSchedule: false,
    savingActivities: false,
    saveSummary: '',
    showActionDialog: false,
    countOfPlannedToSave: 0,
    countOfActualToSave: 0,
    phaseActionsToBeAffected: 'ALL',
  };

  public showActionDialog = false;
  public countOfPlannedToSave = 0
  public countOfActualToSave = 0;
  public showComponent = this.environment.showPadConstructionSchedules;

  public scheduleDataLoaded = false;
  public scheduleActivities = new Array<SurfaceDevelopmentScheduleActivityDto>();
  public payloadClone = new Array<SurfaceDevelopmentScheduleActivityDto>();

  public IsSyncConstruction = false;
  public syncStatusMessage: string = '';
  public userEditedPhases: string[];

  public changedRows: Set<number> = new Set();
  public working: boolean = false;
  public loading: boolean = true;
  private gridApi: GridApi;
  public gridOptions: SurfaceDevScheduleTaskGridOptions;

  public changedRowsToSave: SurfaceDevelopmentScheduleActivityDto[];
  public AllPhases: string[] = ['CONSTRUCT'];
  public dialogTitle: string = '';

  public showActualDatesErrorDialog: boolean;
  public actualDateErrors: string[];

  readonly projectTab = input<IProjectTab>(undefined);
  public disableSave = false;

  public resetButtonData: Array<any> = [
    {
      text: 'ALL',
      svgIcon: arrowRotateCwIcon,
      click: () => { this.PrepareAndShowActionDialog(false, this.AllPhases); },
      disabled: false
    }
  ];

  public onAfterSyncChange(value) {
    this.cdr.detectChanges();
  }

  public async handleCancelButton() {
    await this.getScheduleActivities(); // re-load
  }

  constructor() {

    this.gridOptions = new SurfaceDevScheduleTaskGridOptions();

    for (var phase of this.AllPhases) {
      this.resetButtonData.push(
        {
          text: `${phase}`,
          tag: phase,
          svgIcon: rotateIcon,
          click: ($event) => {
            this.PrepareAndShowActionDialog(false, [$event.tag]);
          }
        }
      )
    }

  }

  public closeDialog() {
    if (this.showActionDialog) {
      this.showActionDialog = false;
      this.cdr.detectChanges();
    }
  }

  public PrepareAndShowActionDialog(trytoSave: boolean, phases: string[]) {
    if (trytoSave) {
      this.changedRowsToSave = this.getChangedRows();

      this.dialogData.savingActivities = this.changedRowsToSave.length > 0;
      this.phasesToSync = phases;
      if (this.dialogData.savingActivities) {
        this.dialogData.saveSummary = `${this.changedRowsToSave.length} Rows will be saved.`;
      }
      this.dialogData.resyncingSchedule = false;
      this.dialogData.savingActivities = true;

    } else {
      this.dialogData.savingActivities = false;
      this.dialogData.resyncingSchedule = true;

      this.dialogData.phaseActionsToBeAffected = '';
      this.phasesToSync = phases;
      if (this.phasesToSync.length == 2 || this.phasesToSync == null || this.phasesToSync.length == 0) {
        this.dialogData.phaseActionsToBeAffected = '[ALL]';
      } else {
        this.dialogData.phaseActionsToBeAffected = `[${this.phasesToSync[0]}]`;
      }
      this.changedRowsToSave = [];
    }

    this.dialogTitle = trytoSave ? 'Save & Reschedule' : 'Reset Schedule';
    this.showActionDialog = true;
    this.cdr.detectChanges();
  }

  public async continueWithAction() {
    this.closeDialog();

    this.working = true;
    try {
      if (this.dialogData.savingActivities && this.changedRowsToSave && this.changedRowsToSave.length) {
        this.actualDateErrors = [];
        this.validateActualDates(this.changedRowsToSave, this.scheduleActivities);
        if (this.actualDateErrors.length > 0) {
          this.showActualDatesErrorDialog = true;
          return;
        }

        let saveResponse = await this.service.saveChangedRows(this.projectTab().ProjectId, this.changedRowsToSave[0].ScheduleId, this.changedRowsToSave);
        if (!saveResponse.Success) {
          this.errorService.display('Error Saving:' + saveResponse.Message);
          return;
        } else {
          //
          //go get the data again, so we get all updates
          await this.getScheduleActivities();
          this.surfacedevPlanningComponent().loadPlanningTasks();
        }
      } else if (this.dialogData.resyncingSchedule) {

        //resync the specific portion
        let resetResponse = await this.service.resetToDrillSchedule(this.projectTab().ProjectId, this.phasesToSync, false);
        if (resetResponse.Success && resetResponse.ScheduleActivities) {
          this.processScheduleActivities(resetResponse.ScheduleActivities, resetResponse.UserEditedPhases);
          this.surfacedevPlanningComponent().loadPlanningTasks();
        } else {
          this.errorService.display(resetResponse.Message);
        }
      }
    }
    catch (response) {
      let error = response as HttpErrorResponse;
      this.errorService.error('Error Resetting Schedule to Drill Schedule:' + error.error, error);
      this.scheduleDataLoaded = false;
    }
    finally {
      this.working = false;
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private validateActualDates(updatedRows: SurfaceDevelopmentScheduleActivityDto[], scheduleActivities: SurfaceDevelopmentScheduleActivityDto[]) {
    updatedRows.forEach(updatedRow => {
      // Compare actual start and finish date of the updated activity
      if (updatedRow.ActualStart && updatedRow.ActualFinish && updatedRow.ActualFinish < updatedRow.ActualStart) {
        this.actualDateErrors.push(`${updatedRow.Phase}/${updatedRow.Step} Actual Finish cannot be earlier than the activity's Actual Start`);
      }

      const predecessor = updatedRow.Predecessor;
      let predecessorActivity: SurfaceDevelopmentScheduleActivityDto;

      if (predecessor == '--') return;

      if (predecessor.includes("/")) {
        // For example, predecessor = 'CONSTRUCT/CONSTRUCTION KICK OFF'
        let phaseStep = predecessor.split("/");
        predecessorActivity = updatedRows.find(activity => activity.Phase == phaseStep[0] && activity.Step == phaseStep[1]);
        if (predecessorActivity == undefined) {
          predecessorActivity = scheduleActivities.find(activity => activity.Phase == phaseStep[0] && activity.Step == phaseStep[1]);
        }
        this.checkActualStartAndFinishDates(updatedRow, predecessorActivity);
      }
      else {
        // For example, predecessor = 'Max([CONSTRUCT_GRADE_PITS],[SPUD-14])'
        let phaseStepsFromPredecessor = this.getValidPhaseStepsFromPredecessor(predecessor);
        phaseStepsFromPredecessor.forEach(phaseStep => {
          let phaseStepId = surfaceDevPhaseSteps[phaseStep];
          predecessorActivity = updatedRows.find(activity => activity.PhaseStepTypeId == phaseStepId);
          if (predecessorActivity == undefined) {
            predecessorActivity = scheduleActivities.find(activity => activity.PhaseStepTypeId == phaseStepId);
          }
          this.checkActualStartAndFinishDates(updatedRow, predecessorActivity);
        });
      }
    });
  }

  private getValidPhaseStepsFromPredecessor(predecessor: string) {
    let startIndex = predecessor.indexOf("(");
    let endIndex = predecessor.lastIndexOf(")");

    if (startIndex > -1 && endIndex > -1) {
      let predecessorPhaseSteps = predecessor.substring(startIndex + 1, endIndex);
      let phaseSteps = predecessorPhaseSteps.split(",").map(str => str.slice(1, -1));

      let validPhaseSteps = phaseSteps.filter(phaseStep => phaseStep.includes("CONSTRUCT"))
      return validPhaseSteps;
    }
  }

  private checkActualStartAndFinishDates(updatedRow: SurfaceDevelopmentScheduleActivityDto, predecessorActivity: SurfaceDevelopmentScheduleActivityDto) {
    if (updatedRow.ActualStart && predecessorActivity && updatedRow.ActualStart < predecessorActivity.ActualFinish) {
      this.actualDateErrors.push(`${updatedRow.Phase}/${updatedRow.Step} Actual Start cannot be earlier than it's predecessor (${predecessorActivity.Phase}/${predecessorActivity.Step}) Actual Finish`);
    }
    if (updatedRow.ActualFinish && predecessorActivity && updatedRow.ActualFinish < predecessorActivity.ActualFinish) {
      this.actualDateErrors.push(`${updatedRow.Phase}/${updatedRow.Step} Actual Finish cannot be earlier than it's predecessor (${predecessorActivity.Phase}/${predecessorActivity.Step}) Actual Finish`);
    }
  }

  public phasesToSync: string[] = null;

  public async handleResetToDrillSchedule(phasesToSync: string[] = ['CONSTRUCT'], saving: boolean, rowsToSave: number) {
    const projectTab = this.projectTab();
    if (!projectTab.ProjectTypeId || !projectTab.ProjectId) {
      throw new Error('Project has not yet loaded. Did you properly await the load?')
    }

    if (phasesToSync.length == 2 || phasesToSync == null || phasesToSync.length == 0) {
      this.dialogData.phaseActionsToBeAffected = '[ALL]';
    } else {
      this.dialogData.phaseActionsToBeAffected = `[${phasesToSync[0]}]`;
    }

    this.showActionDialog = true;
    this.cdr.detectChanges();
  }

  public getChangedRows(): SurfaceDevelopmentScheduleActivityDto[] {
    this.cdr.detectChanges();

    this.dialogData.countOfActualToSave = 0;
    this.dialogData.countOfPlannedToSave = 0;

    let store: SurfaceDevelopmentScheduleActivityDto[] = [];

    this.changedRows.forEach((rowIndex: number) => {

      let currentValue = this.scheduleActivities[rowIndex];
      let originalValue = this.payloadClone[rowIndex];

      if (originalValue.ScheduleActivityId !== currentValue.ScheduleActivityId) {
        throw new Error('Item Mismatch between two Item Ids. Cannot continue.');
      }

      let actualChanged = (originalValue.ActualStart != currentValue.ActualStart ||
        originalValue.ActualFinish != currentValue.ActualFinish ||
        originalValue.ActualDurationDays != currentValue.ActualDurationDays);

      let plannedChange = (originalValue.PlannedStart != currentValue.PlannedStart ||
        originalValue.PlannedFinish != currentValue.PlannedFinish ||
        originalValue.PlannedDurationDays != currentValue.PlannedDurationDays);

      let isConstructAllowed = (currentValue.Phase == 'CONSTRUCT');

      if (!plannedChange && !actualChanged) {
        //do nothing
      } else {
        if (plannedChange) {
          this.dialogData.countOfPlannedToSave++;
        }
        else if (actualChanged) {
          this.dialogData.countOfActualToSave++;
        }

        store.push(currentValue);
      }
    });
    return store;
  }

  private clearArray(data: any[], length: number) {
    if (data && data.length) {
      data.length = 0;
      data.length = length;
    } else if (data) {
      data.length = length;
    } else {
      data = [];
      data.length = length;
    }
  }

  private clearArrays(length: number) {
    this.clearArray(this.payloadClone, length);
  }

  public changeActualDurationFromActualStartAndFinish(value: Date, rowIndex: number): void {
    let data = this.scheduleActivities;
    let one = data[rowIndex];

    let diffDays = dayjs(one.ActualFinish).diff(dayjs(one.ActualStart), 'days');
    if (isNaN(diffDays)) {
      diffDays = 0;
    }
    one.ActualDurationDays = diffDays;

    this.changedRows.add(rowIndex);
  }

  public shiftDates(startingIndex: number, days: number) {
    let data = this.scheduleActivities;
    for (let index = startingIndex; index < data.length; index++) {
      let one = data[index];
      let prior: SurfaceDevelopmentScheduleActivityDto;
      if (index > 0) {
        prior = data[index - 1];
      }
      if (prior.PlannedFinish && one.PlannedStart !== prior.PlannedFinish) {
        one.PlannedStart = prior.PlannedFinish;
        one.PlannedFinish = addDays(one.PlannedStart, one.PlannedDurationDays);
      } else {
        one.PlannedStart = addDays(one.PlannedStart, days);
        if (!one.PlannedFinish) {
          one.PlannedFinish = addDays(one.PlannedStart, days);
        } else {
          one.PlannedFinish = addDays(one.PlannedFinish, days);
        }
      }
      this.changedRows.add(index);
    }
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    await this.getScheduleActivities();
  }

  onFirstDataRendered(params: FirstDataRenderedEvent) {
    this.gridApi.sizeColumnsToFit();
  }

  public changePlannedFinishFromPlannedStartAndDuration(value: number, rowIndex: number): void {
    let row = this.scheduleActivities[rowIndex];
    row.PlannedFinish = addDays(row.PlannedStart, row.PlannedDurationDays);
    this.changedRows.add(rowIndex);
  }

  onCellValueChanged(e: CellValueChangedEvent) {
    if ((e.oldValue == null || e.oldValue === undefined) && (e.newValue == null || e.newValue === undefined)) return;

    let oldValue = '';
    let newValue = '';
    if (e.oldValue == null || e.oldValue === undefined) {
      oldValue = '';
    } else {
      oldValue = e.oldValue;
    }
    if (e.newValue == null || e.newValue === undefined) {
      newValue = '';
    } else {
      newValue = e.newValue;
    }

    if (oldValue.toString() == newValue.toString()) { return; } //necessary for date comparisons. 2 date objects do not return true even if the same

    let rowIndex = this.scheduleActivities.indexOf(e.data);
    let colId = e.column.getColId();

    if (colId == 'PlannedDurationDays' || colId == 'PlannedStart') {
      this.changePlannedFinishFromPlannedStartAndDuration(+e.newValue, rowIndex);

    } else if (colId == 'ActualStart' || colId == 'ActualFinish') {
      this.changeActualDurationFromActualStartAndFinish(e.newValue, rowIndex);
    }

    this.cdr.detectChanges();
    this.gridApi.applyTransaction({ update: [e.data] });
  }

  private async getScheduleActivities() {

    if (!this.projectTab().ProjectTypeId || !this.projectTab().ProjectId) {
      throw new Error('Project has not yet loaded. Did you properly await the load?')
    }

    let url: string = `estimatetaskmapping/surfacedevelopment/${this.projectTab().ProjectId}`;

    try {
      this.loading = true;
      this.cdr.detectChanges();
      let saResult = await this.lookupPlusService.getLookupResultNoCountAsync(url, null, false);

      if (!saResult.Success) {
        this.errorService.error('Error getting Schedule Details', new Error(saResult.Message));
      }
      else {
        this.processScheduleActivities(saResult.Result.ScheduleActivities, saResult.Result.UserEditedPhases);
      }

    }
    catch (response) {
      this.errorService.error('Error getting Schedule Details', response);
      this.scheduleDataLoaded = false;
    }
    finally {
      this.working = false;
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private processScheduleActivities(scheduleActivities: Array<SurfaceDevelopmentScheduleActivityDto>, userEditedPhases: Array<string>) {

    this.scheduleActivities = scheduleActivities;
    this.userEditedPhases = userEditedPhases;

    this.IsSyncConstruction = !userEditedPhases.includes("CONSTRUCT");

    this.syncStatusMessage = this.IsSyncConstruction ? 'Construct Phase Synced to Drill Schedule' : 'Construct Phase Not Synced to Drill Schedule';

    this.payloadClone = [];
    this.changedRows.clear();

    if (!this.scheduleActivities) {
      return;
    }

    this.scheduleActivities.sort((a, b) => a.SortOrder - b.SortOrder);

    // populate the multi-selects
    this.clearArrays(this.scheduleActivities.length);

    for (let i = 0; i < this.scheduleActivities.length; i++) {
      let p = this.scheduleActivities[i];
      if (p !== null && p !== undefined) {
        // make deep copy of each array element
        this.payloadClone[i] = Object.assign({}, p) as SurfaceDevelopmentScheduleActivityDto;
      }
    }
    this.working = false;
    this.loading = false;
    this.scheduleDataLoaded = this.scheduleActivities.length > 0;
    if (!this.gridApi.isDestroyed()) {
      this.gridApi.setGridOption('rowData', this.scheduleActivities);
      this.gridApi.setGridOption('groupTotalRow', "bottom");
      this.gridApi.setGridOption('grandTotalRow', "bottom");
      this.gridOptions.setDefaultFilter(this.gridApi);
    }
    this.cdr.detectChanges();
  }
}